<template>
  <b-modal
    v-model="showModal"
    id="suggestion-modal"
    title="Recommended Prompt Generator"
    size="lg"
    hide-footer
    centered
    modal-class="suggestion-modal-custom"
    @hidden="handleClose"
  >
    <div class="suggestion-modal-body">
      <p class="modal-description">
        Select from the recommended categories provided by the system to create a prompt.
      </p>
      <div
        v-if="promptSuggestionsLoading"
        class="loading-state"
      >
        <div class="spinner-container">
          <b-spinner
            type="border"
            variant="primary"
          />
        </div>
        <p class="loading-text">
          Loading suggestions...
        </p>
      </div>

      <div
        v-else-if="promptSuggestionsError"
        class="error-state"
      >
        <p class="error-text">
          Failed to load suggestions. Please try again.
        </p>
        <b-button
          variant="outline-primary"
          size="sm"
          @click="loadSuggestions"
        >
          Retry
        </b-button>
      </div>

      <div
        v-else-if="Object.keys(categorizedSuggestions).length > 0"
        class="suggestions-content"
      >
        <div class="suggestion-tabs-container">
          <div class="suggestion-tabs">
            <div
              v-for="(suggestions, category) in categorizedSuggestions"
              :key="category"
              :class="['suggestion-tab', { 'active': activeTab === getTabIndex(category) }]"
              @click="setActiveTab(getTabIndex(category))"
            >
              {{ formatCategoryName(category) }}
            </div>
          </div>

          <div class="suggestion-content">
            <div
              v-for="(suggestions, category) in categorizedSuggestions"
              v-show="activeTab === getTabIndex(category)"
              :key="category"
              class="suggestion-tag-list"
            >
              <div
                v-for="suggestion in suggestions"
                :key="suggestion._id"
                :class="['suggestion-tag', { 'selected': isSelected(suggestion) }]"
                @click="toggleSelection(suggestion, category)"
              >
                <span class="tag-text">{{ suggestion.value }}</span>
                <feather-icon
                  v-if="isSelected(suggestion)"
                  icon="CheckIcon"
                  class="tag-check-icon"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-else
        class="no-suggestions"
      >
        <p class="no-suggestions-text">
          No suggestions available.
        </p>
      </div>
    </div>

    <div class="modal-actions">
      <b-button
        variant="outline-secondary"
        size="sm"
        @click="handleClose"
      >
        Cancel
      </b-button>
      <b-button
        :disabled="Object.keys(selectedSuggestions).length === 0"
        variant="outline-primary"
        class="save-button"
        @click="applySelectedSuggestions"
      >
        Save
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BSpinner,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'SuggestionModal',
  components: {
    BModal,
    BButton,
    BSpinner,
    FeatherIcon,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    imageUrl: {
      type: String,
      default: '',
    },
    currentSelectedSuggestions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showModal: this.show,
      selectedSuggestions: {}, // Changed to object to store one selection per category
      activeTab: 0,
    }
  },
  computed: {
    ...mapGetters('videoGeneration', [
      'promptSuggestionsData',
      'promptSuggestionsLoading',
      'promptSuggestionsError',
    ]),
    categorizedSuggestions() {
      // The API response has keys like 'action', 'person', 'region', 'stype', 'background'
      // We want to display them in a specific order if possible, or just iterate
      const order = ['person', 'region', 'background', 'action', 'stype']
      const sortedKeys = Object.keys(this.promptSuggestionsData).sort((a, b) => {
        const indexA = order.indexOf(a)
        const indexB = order.indexOf(b)
        if (indexA === -1 && indexB === -1) return a.localeCompare(b) // Both not in order, sort alphabetically
        if (indexA === -1) return 1 // A not in order, B is, B comes first
        if (indexB === -1) return -1 // B not in order, A is, A comes first
        return indexA - indexB // Both in order, sort by order
      })

      const categorized = {}
      sortedKeys.forEach(key => {
        if (this.promptSuggestionsData[key]) {
          categorized[key] = this.promptSuggestionsData[key]
        }
      })
      return categorized
    },
  },
  watch: {
    show(newVal) {
      this.showModal = newVal
      if (newVal) {
        this.loadSuggestions()
        // Keep previous selections - don't reset selectedSuggestions
        this.activeTab = 0
      }
    },
    showModal(newVal) {
      this.$emit('update:show', newVal)
    },
    categorizedSuggestions: {
      handler(newVal) {
        // Removed setDefaultSelections() - no default selections
        if (newVal && Object.keys(newVal).length > 0) {
          // Categories loaded, but no default selections
        }
      },
      immediate: true,
    },
    currentSelectedSuggestions: {
      handler(newSuggestions) {
        // Update internal selectedSuggestions when parent component changes
        this.updateSelectedSuggestions(newSuggestions)
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions('videoGeneration', ['fetchPromptSuggestions']),
    handleClose() {
      this.showModal = false
      // Keep selectedSuggestions for next time modal opens
      this.$emit('close')
    },
    formatCategoryName(category) {
      // Map API categories to display names as shown in Figma
      const categoryMap = {
        person: 'Choose a person',
        background: 'Environment',
        action: 'Action',
        stype: 'Style',
        region: 'Region',
      }
      return categoryMap[category] || category
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    isSelected(suggestion) {
      const category = this.getCategoryForSuggestion(suggestion)
      return this.selectedSuggestions[category] && this.selectedSuggestions[category]._id === suggestion._id // eslint-disable-line no-underscore-dangle
    },
    toggleSelection(suggestion, category) {
      if (this.isSelected(suggestion)) {
        // If already selected, deselect it
        this.$delete(this.selectedSuggestions, category)
      } else {
        // Select this suggestion and deselect any other in the same category
        this.$set(this.selectedSuggestions, category, suggestion)
      }
    },
    getCategoryForSuggestion(suggestion) {
      // Find which category this suggestion belongs to
      const categories = Object.keys(this.categorizedSuggestions)
      let i = 0
      while (i < categories.length) {
        const category = categories[i]
        const suggestions = this.categorizedSuggestions[category]
        if (suggestions.some(s => s._id === suggestion._id)) { // eslint-disable-line no-underscore-dangle
          return category
        }
        i += 1
      }
      return null
    },
    setActiveTab(tabIndex) {
      this.activeTab = tabIndex
    },
    getTabIndex(category) {
      return Object.keys(this.categorizedSuggestions).indexOf(category)
    },
    applySelectedSuggestions() {
      // Collect the IDs of selected suggestions from all categories
      const selectedArray = Object.values(this.selectedSuggestions).filter(Boolean)
      const promptIds = selectedArray.map(s => s._id) // eslint-disable-line no-underscore-dangle
      const promptText = selectedArray.map(s => s.value).join(', ')
      this.$emit('apply-suggestion', { promptIds, promptText })
      this.handleClose()
    },
    async loadSuggestions() {
      try {
        await this.fetchPromptSuggestions({ limit: 200 })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load suggestions:', error)
      }
    },
    updateSelectedSuggestions(suggestions) {
      // Convert array of suggestions to object format for internal state
      this.selectedSuggestions = {}
      suggestions.forEach(suggestion => {
        const category = this.getCategoryForSuggestion(suggestion)
        if (category) {
          this.$set(this.selectedSuggestions, category, suggestion)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
/* Modal customization */
:deep(.suggestion-modal-custom .modal-content) {
  border-radius: 32px;
  border: none;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.12), 0px -4px 8px 0px rgba(0, 0, 0, 0.12);
}

:deep(.suggestion-modal-custom .modal-header) {
  border-bottom: none;
  padding: 0;
  position: relative;
}

:deep(.suggestion-modal-custom .modal-body) {
  padding: 0;
}

.custom-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.suggestion-modal-body {
  min-height: 400px;
}

.modal-header-content {
  flex: 1;
}

.modal-title-custom {
  font-weight: 400;
  font-size: 20px;
  line-height: 1.1em;
  color: #000000;
  margin: 0 0 8px 0;
}

.modal-description {
  font-weight: 400;
  font-size: 14px;
  line-height: 1.5714285714285714em;
  color: #000000;
  margin: 0;
}

.close-button {
  padding: 0;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #374151;
    background: none;
  }

  &:focus {
    box-shadow: none;
    background: none;
  }
}

.loading-state,
.error-state,
.no-suggestions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  text-align: center;
}

.spinner-container {
  margin-bottom: 1rem;
}

.loading-text {
  color: #6c757d;
  margin: 0;
  font-size: 14px;
}

.error-text {
  color: #dc3545;
  margin: 0 0 1rem 0;
  font-size: 14px;
}

.no-suggestions-text {
  color: #6c757d;
  margin: 0;
  font-style: italic;
}

.suggestions-content {
  min-height: 300px;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.close {
  padding: 0;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6b7280;

  &:hover {
    color: #374151;
  }
}

// Tab styling
:deep(.nav-tabs) {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 1rem;

  .nav-item {
    .nav-link {
      color: #6c757d;
      border: none;
      border-bottom: 2px solid transparent;
      padding: 0.75rem 1rem;
      margin-bottom: -1px;
      font-weight: 500;
      background: none;

      &.active {
        color: #dc2626;
        border-bottom-color: #dc2626;
        background-color: transparent;
      }

      &:hover:not(.active) {
        color: #dc2626;
        border-bottom-color: #fca5a5;
      }
    }
  }
}

:deep(.tab-content) {
  padding-top: 0;
}

.suggestion-tabs-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.suggestion-tabs {
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding-bottom: 0;
}

.suggestion-tab {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.7);
  transition: all 0.2s ease;
  border-bottom: 2px solid transparent;

  &:hover {
    color: rgba(0, 0, 0, 0.9);
  }

  &.active {
    color: #E11C4E;
    border-bottom-color: #E11C4E;
  }
}

.suggestion-content {
  height: 350px;
  overflow-y: auto;
}

.suggestion-tag-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.suggestion-tag {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  gap: 8px;
  padding: 4px 8px;
  border: 1px solid transparent;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgba(0, 0, 0, 0.7);
  border-radius: 4px;

  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  &.selected {
    background-color: rgba(225, 28, 78, 0.05);
    color: rgba(0, 0, 0, 0.7);
  }
}

.tag-text {
  flex: 1;
  font-weight: 400;
}

.tag-check-icon {
  color: #E11C4E;
  width: 20px;
  height: 20px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-top: none;
  .btn {
    border-radius: 20px !important;
    padding: 8px 16px;
    font-weight: 600;
  }

  .btn-outline-secondary {
    border: 1px solid #6c757d !important;
    color: #6c757d !important;
    background: transparent !important;

    &:hover,
    &:focus,
    &:active {
      background: #6c757d !important;
      color: #ffffff !important;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(108, 117, 125, 0.3);
    }
  }
}

.cancel-button {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 16px;
  width: 80px;
  height: 32px;
  background-color: #16213E;
  color: #ffffff;
  border: none;

  &:hover {
    background-color: #1a2747;
    color: #ffffff;
  }

  &:focus {
    background-color: #16213E;
    color: #ffffff;
    box-shadow: none;
  }
}

.save-button {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 100px;
  font-size: 14px;
  width: 80px;
  height: 32px;
  background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%) !important;
  color: #ffffff !important;
  border: none !important;

  &:hover:not(:disabled) {
    background: linear-gradient(141deg, rgba(129, 255, 244, 0.9) 0%, rgba(110, 68, 255, 0.9) 100%) !important;
    color: #ffffff !important;
    border: none !important;
  }

  &:focus {
    background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%) !important;
    color: #ffffff !important;
    box-shadow: none !important;
    border: none !important;
  }

  &:active {
    background: linear-gradient(141deg, rgba(129, 255, 244, 1) 0%, rgba(110, 68, 255, 1) 100%) !important;
    color: #ffffff !important;
    border: none !important;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: linear-gradient(141deg, rgba(129, 255, 244, 0.5) 0%, rgba(110, 68, 255, 0.5) 100%) !important;
    color: #ffffff !important;
  }
}
</style>
